import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Notification } from 'types/notification';
import { markNotificationAsReadAPI } from 'api/notificationsApi';
import { toast } from 'react-toastify';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { formatNotificationContent } from '../../utils/notificationFormat';

interface NotificationDisplayProps {
  notification: Notification;
  onMarkAsRead: (id: number) => void;
}

const getNotificationColor = (type: string) => {
  const colors: { [key: string]: string } = {
    NOTIFICACAO_GERAL: '#CBD5E1',
    TAREFA_ATRIBUIDA: '#BFDBFE',
    TAREFA_STATUS_ALTERADO: '#DDD6FE',
    TAREFA_PRAZO_PROXIMO: '#F87171',
    TAREFA_CONCLUIDA: '#BBF7D0',
    TAREFA_EDITADA: '#DDD6FE',
    TAREFA_REMOVIDA: '#FECACA',
    PROJETO_ATRIBUIDO: '#FEF3C7',
    PROJETO_STATUS_ALTERADO: '#E9D5FF',
    PROJETO_EDITADO: '#FEF3C7',
    PROJETO_ATUALIZADO: '#FEF3C7',
    PROJETO_PRAZO_ALTERADO: '#F87171',
    PROJETO_PRAZO_PROXIMO: '#F87171',
    PROJETO_CONCLUIDO: '#BBF7D0',
    PROJETO_REMOVIDO: '#FECACA',
    SUBTAREFA_ATRIBUIDA: '#BFDBFE',
    SUBTAREFA_CONCLUIDA: '#BBF7D0',
    REQUISICAO_CONFLITO: '#FDE68A',
  };
  return colors[type] || '#E2E8F0';
};

const URGENT_DEADLINE_TYPES = new Set([
  'TAREFA_PRAZO_PROXIMO',
  'PROJETO_PRAZO_ALTERADO',
  'PROJETO_PRAZO_PROXIMO',
]);

const getNotificationTextColor = (type: string) =>
  URGENT_DEADLINE_TYPES.has(type) ? '#F8FAFC' : undefined;

const getNotificationTitle = (type: string) => {
  const titles: { [key: string]: string } = {
    NOTIFICACAO_GERAL: 'Notificação Geral',
    TAREFA_ATRIBUIDA: 'Nova Tarefa Atribuída',
    TAREFA_STATUS_ALTERADO: 'Estado da Tarefa Alterado',
    TAREFA_PRAZO_PROXIMO: 'Prazo de Tarefa Próximo',
    TAREFA_CONCLUIDA: 'Tarefa Concluída',
    TAREFA_EDITADA: 'Tarefa Atualizada',
    TAREFA_REMOVIDA: 'Tarefa Removida',
    PROJETO_ATRIBUIDO: 'Novo Projeto Atribuído',
    PROJETO_STATUS_ALTERADO: 'Estado do Projeto Alterado',
    PROJETO_EDITADO: 'Projeto Atualizado',
    PROJETO_ATUALIZADO: 'Projeto Atualizado',
    PROJETO_PRAZO_ALTERADO: 'Prazo de Projeto Alterado',
    PROJETO_PRAZO_PROXIMO: 'Prazo de Projeto Próximo',
    PROJETO_CONCLUIDO: 'Projeto Concluído',
    PROJETO_REMOVIDO: 'Removido do Projeto',
    SUBTAREFA_ATRIBUIDA: 'Nova Subtarefa Atribuída',
    SUBTAREFA_CONCLUIDA: 'Subtarefa Concluída',
    REQUISICAO_CONFLITO: 'Conflito de Equipamento',
  };
  return titles[type] || type;
};

const NotificationDisplay: React.FC<NotificationDisplayProps> = ({
  notification,
  onMarkAsRead,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate(); // Add this line

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isClickable = !!(notification.tarefa?.id || notification.projeto?.id);

  const handleNotificationClick = () => {
    if (notification.projeto && notification.projeto.id) {
      navigate(`/projetos/${notification.projeto.id}/details`);
    }
  };

  const handleMarkAsRead = useCallback(
    async (event?: React.MouseEvent) => {
      if (event) event.stopPropagation(); // Prevent card click
      if (!notification.isRead && !isSubmitting) {
        setIsSubmitting(true);
        try {
          await markNotificationAsReadAPI(notification.id);
          onMarkAsRead(notification.id);
          toast.success('Notificação marcada como lida', {
            position: isMobile ? 'bottom-center' : 'top-right',
            autoClose: 2000,
          });
        } catch (error) {
          console.error('Error marking notification as read:', error);
          toast.error('Erro ao marcar notificação como lida', {
            position: isMobile ? 'bottom-center' : 'top-right',
          });
        } finally {
          setIsSubmitting(false);
        }
      }
    },
    [notification, onMarkAsRead, isSubmitting, isMobile]
  );

  return (
    <div
      className="notification-card"
      style={
        {
          backgroundColor: getNotificationColor(notification.type),
          opacity: notification.isRead ? 0.7 : 1,
          cursor: isClickable ? 'pointer' : 'default',
          '--notification-text-color': getNotificationTextColor(
            notification.type
          ),
        } as React.CSSProperties
      }
      role="listitem"
      onClick={isClickable ? handleNotificationClick : undefined}
    >
      <div className="notification-header">
        <h3 className="notification-type">
          {getNotificationTitle(notification.type)}
          {isClickable && (
            <FaExternalLinkAlt
              style={{
                marginLeft: 8,
                fontSize: '0.9em',
                verticalAlign: 'middle',
                color: '#2563eb',
              }}
              title="Ir para tarefa ou projeto"
            />
          )}
        </h3>
        <span className="notification-date">
          {new Date(notification.createdAt).toLocaleDateString('pt-PT')}
        </span>
      </div>
      <p className="notification-content">
        {formatNotificationContent(notification.content)}
      </p>
      <div className="notification-details">
        {notification.tarefa && (
          <div className="notification-detail">
            Tarefa: {notification.tarefa.descricao}
          </div>
        )}
        {notification.projeto && (
          <div className="notification-detail">
            Projeto: {notification.projeto.designacao}
          </div>
        )}
      </div>
      {!notification.isRead && (
        <button
          onClick={handleMarkAsRead}
          className="mark-read-button"
          disabled={isSubmitting}
          aria-label={`Mark notification as read: ${notification.content}`}
        >
          {isSubmitting ? 'A processar...' : 'Marcar como lida'}
        </button>
      )}
    </div>
  );
};

export default React.memo(NotificationDisplay);
